import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Lightbulb } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { RULES, TOURNAMENT_TIPS } from "../lib/rulesContent";

const GameRules = ({ r }) => (
  <AccordionItem value={r.game} className="gold-card rounded-xl border-0 px-5" data-testid={`rules-${r.game}`}>
    <AccordionTrigger className="hover:no-underline py-4" data-testid={`rules-${r.game}-toggle`}>
      <div className="flex flex-col items-start gap-1 text-left">
        <div className="font-display text-amber-50 text-base md:text-lg">{r.title}</div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-amber-300/80">{r.tag}</div>
        <div className="text-sm text-slate-400 font-normal">{r.summary}</div>
      </div>
    </AccordionTrigger>
    <AccordionContent className="pb-5" data-testid={`rules-${r.game}-body`}>
      <ol className="flex flex-col gap-3">
        {r.steps.map((s, i) => (
          <li key={i} className="flex gap-3 text-sm text-slate-200 leading-relaxed">
            <span className="font-display gold-text text-lg w-6 shrink-0 leading-none pt-0.5">{i + 1}</span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
      {r.tips?.length > 0 && (
        <div className="mt-4 rounded-lg bg-black/40 border border-amber-300/15 p-3 flex flex-col gap-1.5">
          {r.tips.map((t, i) => (
            <div key={i} className="flex gap-2 text-xs text-amber-100/90"><Lightbulb className="w-3.5 h-3.5 text-amber-300 shrink-0 mt-0.5" /><span>{t}</span></div>
          ))}
        </div>
      )}
    </AccordionContent>
  </AccordionItem>
);

const KIND = { MTT: "Tournament", SNG: "Sit & Go", CASH: "Cash" };
const n = (v) => Number(v || 0).toLocaleString();
// the "!" at a table: the tag and tips describe THAT table (cash/tournament, seats, blinds), not the room's default
const forTable = (r, ctx) => {
  const tour = ctx.kind !== "CASH";
  const blinds = tour ? `blinds ${n(ctx.sb)}/${n(ctx.bb)}, rising` : ctx.stakes;
  const tips = (r.tips || []).filter((t) => !(tour && t.startsWith("Cash games:")));
  return { ...r, tag: [KIND[ctx.kind], `${ctx.seats}-max`, blinds].filter(Boolean).join(" · "), tips: tour ? [...tips, ...TOURNAMENT_TIPS] : tips };
};

const ord = (k) => (k === 1 ? "1st" : k === 2 ? "2nd" : k === 3 ? "3rd" : `${k}th`);
const Row = ({ k, v, id }) => (
  <div className="flex justify-between gap-4 text-sm" data-testid={id}><span className="text-slate-400">{k}</span><span className="text-amber-50 text-right">{v}</span></div>
);
// The "!" at a tournament table: this event's own structure, read from the live tournament
const ThisTournament = ({ t }) => {
  const paid = t.paid_places || t.prize_split || [];
  return (
    <div className="gold-card rounded-xl px-5 py-4 flex flex-col gap-2" data-testid="rules-this-tournament">
      <div className="font-display text-amber-50">{t.name || "This tournament"}</div>
      <Row id="rules-t-stack" k="Starting stack" v={n(t.starting_stack)} />
      {t.level_minutes > 0 && <Row id="rules-t-levels" k="Blind levels" v={`${t.level_minutes} minute${t.level_minutes === 1 ? "" : "s"} each · now level ${t.level}`} />}
      {t.late_reg_levels > 0 && <Row id="rules-t-late" k="Late registration" v={`Until the end of level ${t.late_reg_levels}${t.late_reg_open ? " (open now)" : " (closed)"}`} />}
      <Row id="rules-t-breaks" k="Breaks" v={t.break_every_levels > 0 ? `${t.break_minutes} minutes every ${t.break_every_levels} levels` : "None"} />
      {t.mode && <Row id="rules-t-mode" k="Format" v={t.mode === "FREEZEOUT" ? "Freezeout — no re-buys" : t.mode.replace(/_/g, " ").toLowerCase()} />}
      {paid.length > 0 && <Row id="rules-t-paid" k={`Paid places (${paid.length})`}
        v={paid.map((p, i) => `${ord(p.place || i + 1)} ${typeof p === "number" ? `${p}%` : n(p.amount)}`).join(" · ")} />}
    </div>
  );
};

// Every game in the room, one dropdown each, explained step by step.
// ?game=STUD (the "!" beside a table's name) opens straight on that game.
// `embedded`: rendered inside a window over the table, so it drops the page chrome and the back link.
export default function RulesPage({ embedded = false, game, ctx }) {
  const [search] = useSearchParams();
  const from = useLocation().state?.from;
  const want = game || search.get("game");
  const open = RULES.some((r) => r.game === want) ? want : undefined;
  const [all, setAll] = useState(false);
  const only = ctx && open && !all;
  const list = only ? [forTable(RULES.find((r) => r.game === open), ctx)] : RULES;
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => document.querySelector(`[data-testid="rules-${open}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
    return () => clearTimeout(t);
  }, [open]);
  return (
    <div className={embedded ? "px-5 py-5 flex flex-col gap-6" : "min-h-screen px-6 py-8 max-w-3xl mx-auto flex flex-col gap-6"} data-testid="rules-page">
      {!embedded && (
        <Link to={from?.startsWith("/table/") ? from : "/lobby"} className="btn-gold rounded-full pl-2 pr-3 py-1.5 text-xs font-display tracking-wider flex items-center gap-1.5 self-start btn-lift" data-testid="rules-back">
          <ArrowLeft className="w-4 h-4" /> {from?.startsWith("/table/") ? "Back to table" : "Lobby"}
        </Link>
      )}
      <header className="flex items-center gap-3"><BookOpen className="w-8 h-8 gold-text" /><div>
        <h1 className="font-display text-3xl text-amber-50">{only ? "How this game is played" : "How the games are played"}</h1>
        <p className="text-sm text-slate-400">{only ? "The rules for the table you are at, step by step." : "Open any game for a step-by-step walk-through, from the first chip to the showdown."}</p></div></header>
      {only && ctx.t && <ThisTournament t={ctx.t} />}
      <Accordion type="single" collapsible defaultValue={open} className="flex flex-col gap-3" data-testid="rules-list">
        {list.map((r) => <GameRules key={r.game} r={r} />)}
      </Accordion>
      {ctx && open && <button type="button" onClick={() => setAll((v) => !v)} data-testid="rules-toggle-all"
        className="text-xs text-amber-300 underline underline-offset-2 self-start">{all ? "Show only this table's game" : "See every game in the room"}</button>}
      <p className="text-xs text-slate-500">{only && ctx.kind !== "CASH" ? "Hand rankings and side pots are the same in every game." : "Hand rankings, side pots and the 30-second cash-game re-buy window are the same in every game."} Shuffles are committed to before the deal — see {embedded
        ? <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("pfp-open-page", { detail: { page: "verify" } }))} className="text-amber-300 underline underline-offset-2" data-testid="rules-verify-link">Verify it yourself</button>
        : <Link to="/verify" state={{ from }} className="text-amber-300 underline underline-offset-2" data-testid="rules-verify-link">Verify it yourself</Link>}.</p>
    </div>
  );
}
